import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'

import { keyframes } from '@emotion/core'
import styled from '@emotion/styled'

const borderBlink = keyframes`
	from,
	to {
			border-color: transparent;
	}
	50% {
			border-color: inherit;
	}
`

const SpanWithCursor = styled.span`
	animation: ${borderBlink} 1.5s step-end infinite;
	padding: 0.25rem;
	${({ isCursorVisible }) =>
		isCursorVisible &&
		`
	border-right: 5px solid #aaa;
  `}
`

const DEFAULT_TYPING_SPEED = 125 // lower is faster
const DEFAULT_START_DELAY = 500 // ms to pause at beginning of typing phrase
const DEFAULT_END_DELAY = 2000 // ms to pause at end of typing phrase
const DEFAULT_FINAL_DELAY = 5000 // ms for delay after final phrase

WordListTypingAnimation.propTypes = {
	textList: PropTypes.array.isRequired,
	typingSpeed: PropTypes.number,
	startDelay: PropTypes.number,
	endDelay: PropTypes.number,
	finalDelay: PropTypes.number,
}
export function WordListTypingAnimation({
	textList,
	typingSpeed = DEFAULT_TYPING_SPEED,
	startDelay = DEFAULT_START_DELAY,
	endDelay = DEFAULT_END_DELAY,
	finalDelay = DEFAULT_FINAL_DELAY,
}) {
	const [currentText, setCurrentText] = useState('')
	const [isCursorVisible, setIsCursorVisible] = useState(true)

	const step = useCallback(
		(next) => {
			const { loopNum, isDeleting, txt } = next
			const i = loopNum % textList.length

			// the current part of the phrase to display
			next.txt = textList[i].substring(0, txt.length + (isDeleting ? -1 : 1))

			// set text to render in state
			setCurrentText(next.txt)

			// time between character's render
			// faster x2 when deleting
			let delta = (typingSpeed - Math.random() * 100) / (isDeleting ? 2 : 1)

			// if we aren't deleting and we've spelled out the whole word,
			// wait 2s before proceeding to delete. We can also add
			// a special case here for a longer delay on the last phrase.
			if (!isDeleting && next.txt === textList[i]) {
				next.isDeleting = true
				if (i === textList.length - 1) {
					delta = finalDelay
					setIsCursorVisible(false)
				} else {
					delta = endDelay
				}
			} else if (isDeleting && next.txt === '') {
				setIsCursorVisible(true)

				// otherwise check to see if we've finished deleting the whole
				// phrase and if so, wait half a second and start on the next one
				next.loopNum++
				next.isDeleting = false
				delta = startDelay
			}

			setTimeout(() => {
				step(next)
			}, delta)
		},
		[endDelay, finalDelay, startDelay, textList, typingSpeed]
	)

	useEffect(() => {
		// kick it off
		step({
			loopNum: 0,
			isDeleting: false,
			txt: '',
		})
	}, [step])

	return <SpanWithCursor isCursorVisible={isCursorVisible}>{currentText}</SpanWithCursor>
}
